// FILE: src/components/app/UploadDataComponent/utils/processUploadedData.js
import { slugify } from "../../../../../utils/firebase/core/collections";
import {
  splitTagPathsCell as splitPaths,
  normalizeFullPath,
  segmentsFromPath,
  TAG_PATH_SEP,
  buildTags, // restore hierarchical preview
} from "./tagUtils";

/* ------------------------------- helpers ------------------------------- */

function forEachRow(rows, cb, batchSize = 1000) {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const n = rows.length;
  for (let start = 0; start < n; start += batchSize) {
    const end = Math.min(n, start + batchSize);
    for (let i = start; i < end; i++) cb(rows[i], i);
  }
}

function findHeaderIndex(headers, primary, synonyms = []) {
  const idx = headers.indexOf(primary);
  if (idx >= 0) return idx;
  const lower = headers.map((h) => String(h || "").toLowerCase());
  for (const syn of [primary, ...synonyms]) {
    const i = lower.indexOf(String(syn).toLowerCase());
    if (i >= 0) return i;
  }
  // fuzzy: first header containing the token
  const token = String(primary).toLowerCase();
  const i = lower.findIndex((h) => h.includes(token));
  return i >= 0 ? i : -1;
}

function idxMap(headers) {
  const H = headers || [];
  return {
    id:         findHeaderIndex(H, "ID",        ["transaction id", "tx id"]),
    date:       findHeaderIndex(H, "Date",      ["transaction date", "posted date"]),
    desc:       findHeaderIndex(H, "Description", ["memo", "details", "narration", "payee"]),
    currency:   findHeaderIndex(H, "Currency",  ["currency code", "ccy"]),
    type:       findHeaderIndex(H, "Type",      ["transaction type"]),
    account:    findHeaderIndex(H, "Account",   ["account name", "account"]),
    // NEW: endpoints for transfers
    accountFrom:findHeaderIndex(H, "From Account", ["fromaccount","from account","account from","from"]),
    accountTo:  findHeaderIndex(H, "To Account",   ["toaccount","to account","account to","to"]),
    amount:     findHeaderIndex(H, "Amount",    ["value", "debit", "credit", "outflow", "inflow", "debit/credit", "amt"]),
    tags:       findHeaderIndex(H, "Tags",      ["tag paths", "categories", "category", "labels"]),
  };
}

function parseAmount(v) {
  if (v == null) return 0;
  let s = String(v).trim();
  if (!s) return 0;
  if (/^\(.+\)$/.test(s)) s = "-" + s.slice(1, -1);
  s = s.replace(/\s+/g, "").replace(/,/g, "");
  s = s.replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function toISO(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  const m1 = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (m1) {
    const y = m1[1];
    const m = String(m1[2]).padStart(2, "0");
    const day = String(m1[3]).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return "";
}

function computeAccountBalances(transactions) {
  const centsByAccount = new Map();
  for (const t of transactions) {
    // Only single-leg (non-transfer) rows contribute here; transfer pairs are fanned out by writers later.
    if (!t || String(t.typeName || "").toLowerCase() === "transfer") continue;
    const amt = Number.isFinite(t.amount) ? t.amount : 0;
    const inc = Math.round(amt * 100);
    centsByAccount.set(t.accountId, (centsByAccount.get(t.accountId) || 0) + inc);
  }
  const byAccount = new Map();
  centsByAccount.forEach((cents, accountId) => {
    byAccount.set(accountId, cents / 100);
  });
  return byAccount;
}

function stableId(parts) {
  const s = parts.join("|");
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h.toString(36);
}

/* --------------------------------- main --------------------------------- */

export function processUploadedData({ headers, rows, user, mappingConfig = { defaults: {} } }) {
  const idx = idxMap(headers);
  if (!rows?.length) {
    return {
      accounts: [],
      tags: { nodes: [], tree: [], conflicts: [] },
      transactions: [],
      counts: { accounts: 0, tags: 0, transactions: 0 },
    };
  }

  const defaults = {
    defaultCurrencyCode: mappingConfig?.defaults?.defaultCurrencyCode || "CAD",
    defaultTypeName: mappingConfig?.defaults?.defaultTypeName || "Expense",
    autoGenerateId: !!mappingConfig?.defaults?.autoGenerateId,
  };

  /* ----------------------------- ACCOUNTS ----------------------------- */
  const accountMap = new Map(); // slug -> { name, slug, currencyCode }

  // Helper to register an account name/currency if present
  const registerAccount = (nameCell, currencyCell) => {
    const name = String(nameCell || "").trim();
    if (!name) return;
    const slug = slugify(name);
    const rawCurrency = String(currencyCell || "").trim().toUpperCase();
    const currencyCode = rawCurrency || defaults.defaultCurrencyCode;
    if (!accountMap.has(slug)) {
      accountMap.set(slug, { name, slug, currencyCode });
    } else if (currencyCode && !accountMap.get(slug).currencyCode) {
      accountMap.get(slug).currencyCode = currencyCode;
    }
  };

  forEachRow(rows, (r) => {
    // single account (non-transfer)
    registerAccount((idx.account >= 0 ? r?.[idx.account] : ""), (idx.currency >= 0 ? r?.[idx.currency] : ""));

    // transfer endpoints
    registerAccount((idx.accountFrom >= 0 ? r?.[idx.accountFrom] : ""), (idx.currency >= 0 ? r?.[idx.currency] : ""));
    registerAccount((idx.accountTo   >= 0 ? r?.[idx.accountTo]   : ""), (idx.currency >= 0 ? r?.[idx.currency] : ""));
  });

  const accounts = Array.from(accountMap.values());

  /* ------------------------------- TAGS ------------------------------- */
  // Collect *all* normalized full paths first
  const allPaths = [];
  forEachRow(rows, (r) => {
    const rawCell = idx.tags >= 0 ? r?.[idx.tags] : "";
    const paths = splitPaths(rawCell).map((p) => normalizeFullPath(p));
    if (paths.length) allPaths.push(...paths);
  });

  // Build nodes + tree exactly like before (restores hierarchical preview)
  const { nodes: tagNodes, tree: tagTree } = buildTags(allPaths);

  /* --------------------------- TRANSACTIONS --------------------------- */
  const transactions = [];
  forEachRow(rows, (r, i) => {
    const idRaw       = idx.id >= 0 ? (r?.[idx.id] ?? "") : "";
    const date        = toISO((idx.date >= 0 ? r?.[idx.date] : "").toString().trim());
    const description = (idx.desc >= 0 ? r?.[idx.desc] : "").toString().trim();

    const currencyCell = (idx.currency >= 0 ? r?.[idx.currency] : "").toString().trim().toUpperCase();
    const currencyCode = currencyCell || defaults.defaultCurrencyCode;

    const typeCell = (idx.type >= 0 ? r?.[idx.type] : "").toString().trim();
    const typeName = (typeCell || defaults.defaultTypeName).toString().trim();

    const accountName = (idx.account >= 0 ? r?.[idx.account] : "").toString().trim();
    const accountId = accountName ? slugify(accountName) : "";

    const fromName = (idx.accountFrom >= 0 ? r?.[idx.accountFrom] : "").toString().trim();
    const toName   = (idx.accountTo   >= 0 ? r?.[idx.accountTo]   : "").toString().trim();
    const fromAccountId = fromName ? slugify(fromName) : "";
    const toAccountId   = toName   ? slugify(toName)   : "";

    const rawAmount = parseAmount(idx.amount >= 0 ? r?.[idx.amount] : "");
    const amountAbs = Math.abs(rawAmount);

    // All tag paths (normalized full paths)
    const tagPaths = splitPaths(idx.tags >= 0 ? r?.[idx.tags] : "").map((p) => normalizeFullPath(p));

    // Primary path for legacy fields (kept for consistency with prior preview)
    const primary = tagPaths[0] || "";
    const segs = segmentsFromPath(primary); // ["A","B","C"]
    const cumPaths = segs.map((_, j) => segs.slice(0, j + 1).join(TAG_PATH_SEP)); // ["A","A / B","A / B / C"]
    const tagPathIds = cumPaths.map((fp) => slugify(fp));
    const tagLeafId = tagPathIds[tagPathIds.length - 1] || null;

    const isTransfer = String(typeName).toLowerCase() === "transfer";
    const hasBothEndpoints = !!fromAccountId && !!toAccountId;

    if (isTransfer && hasBothEndpoints) {
      // Emit **single consolidated transfer row**
      const tx = {
        id: String(idRaw || "").trim() || undefined,
        date,
        description,
        currencyCode,
        typeName: "Transfer",

        fromAccountId,
        toAccountId,
        fromAccountName: fromName || undefined,
        toAccountName: toName || undefined,

        amountAbs, // positive
        // keep tags
        tagPath: segs,
        tagPathIds,
        tagAncestorIds: tagPathIds,
        tagLeafId,
        tagPaths, // all paths for preview clarity

        source: "csv-upload",
        sourceRowIndex: i,
      };

      if (!tx.id && defaults.autoGenerateId) {
        tx.id = stableId([date, description, amountAbs, fromAccountId, toAccountId, currencyCode]);
      }

      transactions.push(tx);
    } else {
      // Non-transfer — legacy single-leg shape unchanged
      const tx = {
        id: String(idRaw || "").trim() || undefined,
        date,
        description,
        currencyCode,
        typeName,
        accountName,
        accountId: accountId || slugify("account"),
        amount: rawAmount,

        tagPaths,              // all imported paths (preview clarity)
        tagPath: segs,         // legacy primary path segments
        tagPathIds,            // cumulative slugs for primary path
        tagAncestorIds: tagPathIds,
        tagLeafId,

        source: "csv-upload",
        sourceRowIndex: i,
      };

      if (!tx.id && defaults.autoGenerateId) {
        tx.id = stableId([date, description, rawAmount, tx.accountId, currencyCode]);
      }

      transactions.push(tx);
    }
  });

  // Balances from transactions (single-leg only)
  const balancesByAccount = computeAccountBalances(transactions);
  const accountsWithBalance = accounts.map((a) => ({
    ...a,
    accountBalance: balancesByAccount.get(a.slug) ?? 0,
  }));

  return {
    accounts: accountsWithBalance,
    tags: { nodes: tagNodes, tree: tagTree, conflicts: [] },
    transactions,
    counts: {
      accounts: accountsWithBalance.length,
      tags: tagNodes.length,
      transactions: transactions.length,
    },
  };
}
